import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../commons/contexts/global/global-provider';
import useLoadVendors from '../../landing-autos/hooks/use-load-vendors';
import { IOption } from '../../quotation/constants/banks';
import { useDebounce } from 'use-debounce';
import { useCreateOpportunity } from 'domains/core-base/crm-oracle/hooks/mutation/use-create-opportunity';
import Utils from 'libs/common/utils';
import { DataLayer } from '../../../libs/providers/datalayer/datalayer';
import useInitAuthentication from 'domains/core-base/authentication/hooks/queries/use-authentication';
import { ORACLE_ACTION } from 'domains/commons/contexts/global/staticData';
import { getCurrentDay } from 'domains/landing-autos/utils';
import { getIdDocumentType } from 'domains/core-base/crm-oracle/utils';
import { useFetchCustomer } from 'domains/core-base/customer/hooks/queries/use-fetch-customer';
import {
  CE_VALUES,
  DNI_VALUES,
} from 'domains/quotation/constants/document-types';
import { imageBanner, bannerMobileBackground } from '../utils/banner';
import {
  AE_OFFER_CODE_MAIN,
  AE_OFFER_CODE_SECONDARY,
} from '../constants/plans';
import { getAutoefectivoName, getAutoefectivoPath } from '../utils/datalayer';
import { getURLRedirectChannelPage } from 'libs/utils/redirectChannel';
import { CrmPhase } from 'domains/landing-autos/enums/crm.enum';
import { usePatchPhaseOpportunity } from 'domains/core-base/crm-oracle/hooks/mutation/use-patch-phase-opportunity';

export const useBanner = () => {
  const { actions, store } = useContext(AppContext);

  const navigateTo = useNavigate();
  const { channel = null } = useParams();

  useInitAuthentication();
  useLoadVendors();

  const location = useLocation();
  const { pathname } = location;

  const [fields, setFields] = useState({
    documentType: 'DNI',
    dni: '',
    phone: '',
    firstName: '',
    email: '',
    dniValid: false,
    phoneValid: false,
    firstNameValid: false,
    emailValid: false,
  });
  const name = getAutoefectivoName(pathname);
  const mainImage = imageBanner(pathname);
  const mainImageMobile = bannerMobileBackground(pathname);
  const [hasConsent, setHasConsent] = useState(false);

  const [showNameField, setShowNameField] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
  const [isLoadingDNI, setIsLoadingDNI] = useState<boolean>(false);

  const [documentType, setDocumentType] = useState({
    label: 'DNI',
    value: 'DNI',
  });

  const [currentDocument] = useDebounce(fields.dniValid ? fields.dni : '', 500);

  const oracleCreateOpportunity = useCreateOpportunity();
  const oracleOfferUpdate = usePatchPhaseOpportunity();

  const { data: customer } = useFetchCustomer({
    documentType: fields.documentType,
    documentId: currentDocument,
  });

  const handleChangeHasConsent = () => {
    setHasConsent(!hasConsent);
  };

  useEffect(() => {
    if (
      fields.dni.length === DNI_VALUES.length ||
      fields.dni.length === CE_VALUES.length
    ) {
      setIsLoadingDNI(true);
      if (customer && customer?.verified) {
        setShowNameField(!customer.names);
        const data = {
          ...fields,
          firstName: customer?.names ?? '',
          firstNameValid: true,
        };
        setFields(data);
        setIsLoadingDNI(false);
      }
      if (customer && !customer.verified) {
        const data = {
          ...fields,
          firstName: '',
        };
        setFields(data);
        setShowNameField(true);
        setIsLoadingDNI(false);
      }
    } else {
      const data = {
        ...fields,
        firstName: '',
        dniValid: false,
      };

      setFields(data);
      setShowNameField(false);
    }
  }, [customer, fields.dni]);


  const changeValidState = (key: string, valid: boolean) => {
    setFields((state) => ({ ...state, [key]: valid }));
  };

  const handleDocumentType = (document: IOption) => {
    const data = {
      ...fields,
      documentType: document.value,
    };

    setDocumentType(document);
    setFields(data);
  };

  const handleOnChange = (val: any, field: any, regex?: any) => {
    let valueFromInput = val;

    if (regex) {
      valueFromInput = val.replace(regex, '');
    }

    setFields({
      ...fields,
      [field]: valueFromInput,
    });
  };

  const handleChangeMobile = (event: any) => {
    const data = {
      ...fields,
      phone: Utils.stripNumbers(event.target.value),
    };
    setFields(data);
  };

  const formValid = Object.entries(fields).every(([_, value]) => value);

  const handleSubmit = async () => {
    setIsLoadingForm(true);
    DataLayer.setVirtualEvent({
      category: 'Seguro Vehicular - Plan Auto Efectivo Leads',
      action: 'Inicio',
      label: `Click - Botón - Solicítalo aquí - ${store.newPlan}`,
      flujo: `${store.newPlan}`,
      tipoZona: 'Autos',
      zona: `PlanAutoefectivo${name}Leads`,
      subZona: 'Inicio',
      sección: 'Formulario',
      tipoEvento: 'click',
      tipoElemento: 'Botón',
      valor: `Solicítalo aquí - ${store.newPlan} `,
    });

    try {
      const isMainChannel = getAutoefectivoPath(pathname) === '';

      const optyNumber = await oracleCreateOpportunity.mutateAsync({
        tCampanaEC_c: 'Campaña auto MVP',
        tECId_c: store.request.id, // request id
        tECConsentimiento_c: hasConsent,
        tECFrom_c: window.location.search ?? '', // ?from de la ruta
        dEC_c: getCurrentDay('YYYY-MM-DD'), // fecha actual
        chECFase_c: CrmPhase.form, // fase actual

        // datos obligatorios cliente
        chPerTipoDoc_c: String(getIdDocumentType(fields.documentType)),
        tPerNumDoc_c: currentDocument,

        // datos opcionales cliente
        tPerNombre_c: fields.firstName,
        tPerTelefonoCelular_c: fields.phone,
        tPerCorreoElectronico_c: fields.email,
      });

      await oracleOfferUpdate.mutateAsync({
        data: {
          chECFase_c: CrmPhase.form,
          tCodigoDeOferta_c: isMainChannel
            ? AE_OFFER_CODE_MAIN
            : AE_OFFER_CODE_SECONDARY,
        },
        options: {
          optyNumber,
        },
      });

      actions.dispatchOracle({
        type: ORACLE_ACTION.setOptyNumber,
        payload: {
          optyNumber,
        },
      });
      navigateTo({
        pathname: getURLRedirectChannelPage('gracias', channel!),
        search: window.location.search,
      });
      setIsLoadingForm(false);
    } catch (err) {
      setIsLoadingForm(false);
    }
  };

  const handleScrollPlans = () => {
    let access = document.getElementById('plans-table');
    access?.scrollIntoView({ behavior: 'smooth' });
  };

  return {
    fields,
    showNameField,
    handleOnChange,
    documentType,
    handleDocumentType,
    handleSubmit,
    isLoadingForm,
    changeValidState,
    handleChangeMobile,
    formValid,
    handleScrollPlans,
    hasConsent,
    handleChangeHasConsent,
    isLoadingDNI,
    mainImage,
    mainImageMobile,
  };
};
